// conversationSummary — the two pure transitions the conversation-rename lane
// needs, extracted so the impure shell (`AppShellHost`) stays a thin caller and
// the SEMANTICS are testable (a pure, testable map + fallback, the same shape
// the pane-tree and chat-session helpers use).
//
//   • `titleForConversation` — the tab-strip / rail label projection: a
//     conversation's own summary title, or a caller-supplied fallback before a
//     summary has loaded / for a just-created thread. The rail rows and the tab
//     label both read THIS one source, so a rename that patches the summary
//     projects to both at once.
//   • `retitleConversation` — the optimistic patch a submitted rename applies
//     to the summaries in place (everything-must-feel-fast), before the port
//     round-trips and reconciles.
//
// Generic over `{ id; title }` so it works on the host's `RecentThread[]`
// without coupling to that shape; the returned array preserves each entry's
// other fields (`when`, …) verbatim.

import { Remote } from "@bilda/utils";

type Titled = { readonly id: string; readonly title: string };

// === The list as a remote read (G6) ===
//
// The rail's conversation list is a value loaded from the server, so it is a
// `Remote`: a failed `listConversations` (a 429, the network) is `Failed`,
// never an empty `Loaded([])` — "no conversations yet" must mean exactly that,
// not "the read failed". Emptiness is a property of the loaded value.
//
//   • `summariesOf` — the plain array the many non-rail readers (tab labels,
//     search, durability) project from: the held value, or none.
//   • `withStartedConversation` — a just-started conversation joins a held
//     list (newest first, idempotent). With no list held it is `null`: the
//     caller re-reads instead, since only the server can supply the rest.

export type ConversationList<T, E> = Remote.Remote<readonly T[], E>;

export const summariesOf = <T, E>(list: ConversationList<T, E>): readonly T[] =>
    Remote.match<readonly T[], E, readonly T[]>({
        notAsked: () => [],
        loading: () => [],
        reloading: (prev) => prev,
        loaded: (value) => value,
        failed: () => [],
    })(list);

// What the rail shows for the list: nothing yet (`pending` — a boot read is a
// few hundred ms, a placeholder would only flash), the rows (`ready`, which may
// be empty: "no conversations yet"), or a quiet inline error with a retry
// (`failed`). A reload in flight keeps showing the held rows.
export type RailListStatus = "pending" | "ready" | "failed";

export const railListStatus = <T, E>(
    list: ConversationList<T, E>,
): RailListStatus =>
    Remote.match<readonly T[], E, RailListStatus>({
        notAsked: () => "pending",
        loading: () => "pending",
        reloading: () => "ready",
        loaded: () => "ready",
        failed: () => "failed",
    })(list);

// Whether a conversation is durable (has a server aggregate) — KNOWN only while
// the list is held. A list still loading, or one that failed, cannot tell a
// durable conversation from a nascent one, so it answers `unknown` (D50: the URL
// mirror read "not in an unheld list" as nascent and rewrote `/c/:id` to `/`).
export type Durability = "durable" | "nascent" | "unknown";

export const durabilityOf = <T extends Titled, E>(
    list: ConversationList<T, E>,
    conversationId: string,
): Durability => {
    const within = (held: readonly T[]): Durability =>
        held.some((c) => c.id === conversationId) ? "durable" : "nascent";
    return Remote.match<readonly T[], E, Durability>({
        notAsked: () => "unknown",
        loading: () => "unknown",
        reloading: within,
        loaded: within,
        failed: () => "unknown",
    })(list);
};

export const withStartedConversation = <T extends Titled, E>(
    list: ConversationList<T, E>,
    started: T,
): ConversationList<T, E> | null => {
    const prepend = (prev: readonly T[]): readonly T[] =>
        prev.some((c) => c.id === started.id) ? prev : [started, ...prev];
    return Remote.match<readonly T[], E, ConversationList<T, E> | null>({
        notAsked: () => null,
        loading: () => null,
        reloading: (prev) => Remote.reloading(prepend(prev)),
        loaded: (value) => Remote.loaded(prepend(value)),
        failed: () => null,
    })(list);
};

export const titleForConversation = <T extends Titled>(
    summaries: readonly T[],
    id: string,
    fallback: string,
): string => {
    // F63 — a conversation's summary title is the first-message excerpt, which
    // is EMPTY for a thread with no user message yet. Treat empty/whitespace as
    // "no title" so a nascent thread reads the neutral fallback, not a blank
    // label (a bare `?? fallback` would miss the empty string).
    const title = summaries.find((c) => c.id === id)?.title;
    return title === undefined || title.trim() === "" ? fallback : title;
};

export const retitleConversation = <T extends Titled>(
    summaries: readonly T[],
    id: string,
    title: string,
): readonly T[] => summaries.map((c) => (c.id === id ? { ...c, title } : c));

// === Archive (set-aside) lane ===
//
//   • `partitionByArchive` — the rail's default list vs its "Archived"
//     section: archived threads drop out of the default list but stay
//     reachable (recoverable), never hidden outright. Order is preserved.
//   • `markArchived` — the optimistic patch an archive / unarchive applies to
//     the summaries in place before the port round-trips and reconciles.

type Archivable = Titled & { readonly archived?: boolean };

export const partitionByArchive = <T extends Archivable>(
    summaries: readonly T[],
): { readonly active: readonly T[]; readonly archived: readonly T[] } => ({
    active: summaries.filter((c) => c.archived !== true),
    archived: summaries.filter((c) => c.archived === true),
});

// The rail's list is ONE view at a time: the active conversations, or the
// archived ones (a view-ephemeral toggle, never domain state).
export type RailListMode = "active" | "archived";

export const toggleRailListMode = (mode: RailListMode): RailListMode =>
    mode === "active" ? "archived" : "active";

export const rowsForMode = <T extends Archivable>(
    summaries: readonly T[],
    mode: RailListMode,
): readonly T[] => partitionByArchive(summaries)[mode];

export const markArchived = <T extends Archivable>(
    summaries: readonly T[],
    id: string,
    archived: boolean,
): readonly T[] => summaries.map((c) => (c.id === id ? { ...c, archived } : c));
